// src/config/routes.config.tsx

/**
 * Tipos de roles del sistema
 */
export type UserRole =
  | 'Administrador_Calidad'
  | 'Responsable_Calidad'
  | 'Auditor_Interno'
  | 'Creador_Documentos'
  | 'Aprobador'
  | 'Revisor'
  | 'Lector';

/**
 * Definición de una ruta
 */
export interface RouteDefinition {
  path: string;
  label: string;
  icon?: string;
  requiresAuth: boolean;
  allowedRoles?: UserRole[];
}

/**
 * Rutas públicas (sin autenticación)
 */
export const publicRoutes: RouteDefinition[] = [
  {
    path: '/login',
    label: 'Iniciar Sesión',
    requiresAuth: false,
  },
];

/**
 * Rutas por rol
 */
export const roleBasedRoutes: Record<UserRole, RouteDefinition[]> = {
  Administrador_Calidad: [
    {
      path: '/quality/dashboard',
      label: 'Dashboard',
      icon: 'Award',
      requiresAuth: true,
      allowedRoles: ['Administrador_Calidad', 'Responsable_Calidad'],
    },
    {
      path: '/quality/documents',
      label: 'Gestión de Documentos',
      icon: 'FileText',
      requiresAuth: true,
      allowedRoles: ['Administrador_Calidad', 'Responsable_Calidad'],
    },
    {
      path: '/admin/app-change-control',
      label: 'Control de Cambios de la Aplicación',
      icon: 'GitBranch',
      requiresAuth: true,
      allowedRoles: ['Administrador_Calidad'],
    },
    {
      path: '/admin/whatsapp',
      label: 'Notificaciones WhatsApp',
      icon: 'MessageCircle',
      requiresAuth: true,
      allowedRoles: ['Administrador_Calidad'],
    },
    {
      path: '/audit/history',
      label: 'Historial de Auditoría',
      icon: 'Activity',
      requiresAuth: true,
      allowedRoles: ['Administrador_Calidad', 'Auditor_Interno'],
    },
  ],

  Responsable_Calidad: [
    {
      path: '/quality/dashboard',
      label: 'Panel de Calidad',
      icon: 'Award',
      requiresAuth: true,
      allowedRoles: ['Responsable_Calidad'],
    },
    {
      path: '/quality/documents',
      label: 'Gestión de Documentos',
      icon: 'FileText',
      requiresAuth: true,
      allowedRoles: ['Responsable_Calidad'],
    },
  ],

  Auditor_Interno: [
    {
      path: '/audit/history',
      label: 'Historial Completo',
      icon: 'Activity',
      requiresAuth: true,
      allowedRoles: ['Auditor_Interno'],
    },
    {
      path: '/documents/view',
      label: 'Consultar Documentos',
      icon: 'BookOpen',
      requiresAuth: true,
      allowedRoles: ['Auditor_Interno', 'Lector'],
    },
  ],

  Creador_Documentos: [
    {
      path: '/creator/dashboard',
      label: 'Mis Documentos',
      icon: 'Edit',
      requiresAuth: true,
      allowedRoles: ['Creador_Documentos'],
    },
    {
      path: '/documents/view',
      label: 'Consultar Documentos',
      icon: 'BookOpen',
      requiresAuth: true,
      allowedRoles: ['Creador_Documentos', 'Lector'],
    },
  ],

  Aprobador: [
    {
      path: '/documents/view',
      label: 'Consultar Documentos',
      icon: 'BookOpen',
      requiresAuth: true,
      allowedRoles: ['Aprobador', 'Lector'],
    },
  ],

  Revisor: [
    {
      path: '/documents/view',
      label: 'Consultar Documentos',
      icon: 'BookOpen',
      requiresAuth: true,
      allowedRoles: ['Revisor', 'Lector'],
    },
  ],

  Lector: [
    {
      path: '/documents/view',
      label: 'Biblioteca de Documentos',
      icon: 'BookOpen',
      requiresAuth: true,
      allowedRoles: ['Lector'],
    },
  ],
};

/**
 * Obtiene las rutas permitidas para un rol específico
 */
export const getRoutesForRole = (role: UserRole): RouteDefinition[] => {
  return roleBasedRoutes[role] || [];
};

/**
 * Obtiene el dashboard por defecto para un rol
 */
export const getDefaultRouteForRole = (role: UserRole): string => {
  const routes = getRoutesForRole(role);
  return routes.length > 0 ? routes[0].path : '/login';
};

/**
 * Verifica si un usuario tiene acceso a una ruta
 */
export const hasAccessToRoute = (
  userRole: UserRole,
  routePath: string
): boolean => {
  const userRoutes = getRoutesForRole(userRole);
  return userRoutes.some((route) => route.path === routePath);
};

/**
 * Rutas compartidas (accesibles por múltiples roles)
 */
export const sharedRoutes: RouteDefinition[] = [
  {
    path: '*',
    label: 'Página No Encontrada',
    requiresAuth: false,
  },
];

/**
 * Todas las rutas del sistema
 */
export const getAllRoutes = (): RouteDefinition[] => {
  const allRoleRoutes = Object.values(roleBasedRoutes).flat();
  return [...publicRoutes, ...allRoleRoutes, ...sharedRoutes];
};
